import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../components/ui/Toast';
import Turnstile from 'react-turnstile';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import Logo from '../components/Logo';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const { requestMagicLink, verifyMagicLink, currentUser } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      handleTokenVerification(token);
    }
  }, [location]);

  useEffect(() => {
    if (currentUser) {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const handleTokenVerification = async (token: string) => {
    setIsLoading(true);
    try {
      await verifyMagicLink(token);
      navigate('/dashboard');
      toast({
        title: "Authentication successful",
        description: "You have been logged in successfully.",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Authentication failed",
        description: "Invalid or expired magic link.",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !turnstileToken) return;

    setIsLoading(true);
    try {
      await requestMagicLink(email, turnstileToken);
      setMagicLinkSent(true);
      toast({
        title: "Magic link sent",
        description: "Check your email for the login link.",
        type: "success"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send magic link. Please try again.",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-132px)] flex flex-col justify-center items-center px-4">
      <div className="p-8 w-full max-w-md rounded-lg border shadow-xl bg-dark-800 border-dark-700">
        <div className="flex justify-center mb-6">
          <Logo className="w-20 h-20" />
        </div>
        <h2 className="mb-2 text-2xl font-bold text-center text-dark-100">
          WinMax License Portal
        </h2>
        <p className="mb-8 text-center text-dark-400">
          Sign in to manage your WinMax licenses
        </p>

        {magicLinkSent ? (
          <div className="p-4 text-center rounded-lg border bg-primary-900/20 border-primary-900/50">
            <Mail className="mx-auto mb-2 w-8 h-8 text-primary-500" />
            <h3 className="mb-1 text-lg font-medium text-primary-400">Check your inbox</h3>
            <p className="mb-3 text-sm text-primary-300">
              We've sent a magic link to <strong>{email}</strong>
            </p>
            <p className="text-xs text-primary-400">
              Click the link in the email to sign in to your account.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              type="email"
              label="Email address"
              icon={Mail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              fullWidth
            />

            <Button
              type="submit"
              disabled={isLoading || !email.trim() || !turnstileToken}
              loading={isLoading}
              icon={Mail}
              fullWidth
            >
              Send magic link
            </Button>
          </form>
        )}
      </div>

      <Turnstile
        sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
        onSuccess={setTurnstileToken}
        theme="dark"
        appearance="interaction-only"
      />
    </div>
  );
};

export default Login;